import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type WaOptInTrigger = 'firstLoad' | 'browsing' | 'afterApplication' | 'declineExit';

interface WhatsAppOptInContextValue {
  trigger?: WaOptInTrigger;
  hasBrowsedEnoughJobPositions: boolean;
  open: (trigger: WaOptInTrigger) => void;
  close: () => void;
  addViewedJobPosition: (jobPositionId: string) => void;
  markReachedFifthJobPosition: () => void;
}

export const WhatsAppOptInContext = createContext<WhatsAppOptInContextValue | undefined>(undefined);

const VIEWED_JOB_POSITIONS_TO_TRIGGER = 2;

export const WhatsAppOptInProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trigger, setTrigger] = useState<WaOptInTrigger | undefined>(undefined);
  const [hasBrowsedEnoughJobPositions, setHasBrowsedEnoughJobPositions] = useState(false);
  const viewedJobPositionIds = useRef(new Set<string>());

  const open = useCallback((nextTrigger: WaOptInTrigger) => setTrigger(nextTrigger), []);
  const close = useCallback(() => setTrigger(undefined), []);

  const addViewedJobPosition = useCallback((jobPositionId: string) => {
    if (viewedJobPositionIds.current.size >= VIEWED_JOB_POSITIONS_TO_TRIGGER) return;
    viewedJobPositionIds.current.add(jobPositionId);
    if (viewedJobPositionIds.current.size >= VIEWED_JOB_POSITIONS_TO_TRIGGER) setHasBrowsedEnoughJobPositions(true);
  }, []);

  const markReachedFifthJobPosition = useCallback(() => {
    setHasBrowsedEnoughJobPositions(true);
  }, []);

  const value = useMemo(
    () => ({
      trigger,
      open,
      close,
      addViewedJobPosition,
      hasBrowsedEnoughJobPositions,
      markReachedFifthJobPosition,
    }),
    [trigger, open, close, addViewedJobPosition, hasBrowsedEnoughJobPositions, markReachedFifthJobPosition],
  );

  return <WhatsAppOptInContext.Provider value={value}>{children}</WhatsAppOptInContext.Provider>;
};

export const useWhatsAppOptInContext = (): WhatsAppOptInContextValue => {
  const context = useContext(WhatsAppOptInContext);
  if (!context) throw new Error('useWhatsAppOptInContext must be used within a WhatsAppOptInProvider');
  return context;
};
